/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { maxDate, minDate } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment/min/moment-with-locales'

const DATE_FORMAT = 'YYYY-MM-DD'

@Component({
  selector: 'myprofile-birthday-editform',
  templateUrl: './myprofile-birthday-editform.component.html',
  host: { class: 'profile:block profile:relative', '[attr.tabindex]': '-1' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileBirthdayEditformComponent implements OnInit {
  private _translateService = inject(TranslateService)

  @Input() minDate?: string
  @Input() maxDate?: string
  @Input() dateToEdit!: string
  @Input() errorMessage!: string
  @Input() errorMessageTranslations!: { [index: string]: any }

  @Output() cancelBirthday = new EventEmitter<boolean>()
  @Output() saveBirthday = new EventEmitter<string>()

  public dateOfBirthFormGroup: UntypedFormGroup
  public shouldShowErrorMessage = false

  constructor() {
    this.dateOfBirthFormGroup = new UntypedFormGroup({
      dateOfBirth: new UntypedFormControl(''),
    })
  }

  get dateOfBirthFormControlErrorMessage() {
    const dateOfBirthFormControl = this.dateOfBirthFormGroup.get('dateOfBirth')!

    if (dateOfBirthFormControl.valid || !dateOfBirthFormControl.touched) {
      return false
    }

    if (dateOfBirthFormControl.errors?.required) {
      return this._translateService.instant('birthdayWidget.input.required')
    }

    if (dateOfBirthFormControl.errors?.maxDate) {
      return this._translateService.instant('birthdayWidget.input.maxDate')
    }

    if (dateOfBirthFormControl.errors?.minDate) {
      return this._translateService.instant('birthdayWidget.input.minDate')
    }

    if (dateOfBirthFormControl.errors?.invalid) {
      return this._translateService.instant('birthdayWidget.input.invalid')
    }
  }

  static triggerFormValidationErrors(formGroup: UntypedFormGroup) {
    Object.entries(formGroup.controls)
      .filter(([_, control]) => !control.disabled)
      .forEach(([controlName]) => {
        const element = document.querySelector(`[name="${controlName}"]`) as HTMLElement | null
        if (element) {
          element.focus()
          element.blur()
        }
      })

    Object.values(formGroup.controls)
      .filter((ctrl) => !ctrl.disabled)
      .forEach((ctrl) => {
        ctrl.markAllAsTouched()
      })
  }

  async ngOnInit() {
    if (this.dateToEdit) {
      // Design component expects YYYY-MM-DD format
      const momentDate = moment(this.dateToEdit, 'YYYYMMDD', true)

      if (!momentDate.isValid()) {
        this.dateOfBirthFormGroup.controls.dateOfBirth.setValue('')
      } else {
        const date = momentDate.format(DATE_FORMAT)
        this.dateOfBirthFormGroup.controls.dateOfBirth.setValue(date)
      }
    } else {
      this.dateOfBirthFormGroup.controls.dateOfBirth.setValue('')
    }
    this.setupFormValidation()
  }

  setupFormValidation() {
    const validators = [Validators.required]
    const formControl = this.dateOfBirthFormGroup.get('dateOfBirth')!

    if (this.maxDate) {
      validators.push(maxDate(this.maxDate, DATE_FORMAT))
    }

    if (this.minDate) {
      validators.push(minDate(this.minDate, DATE_FORMAT))
    }

    formControl.setValidators(validators)
  }

  triggerCancel() {
    this.cancelBirthday.emit(false)
  }

  submit() {
    if (this.dateOfBirthFormGroup.invalid) {
      MyProfileBirthdayEditformComponent.triggerFormValidationErrors(this.dateOfBirthFormGroup)
      return
    }

    const newDate = this.dateOfBirthFormGroup.get('dateOfBirth')!.value
    this.saveBirthday.emit(newDate)
    this.shouldShowErrorMessage = true
  }

  onDateChange(event: CustomEvent<any>) {
    let value = ''
    if (event.target && typeof (event.target as any).value === 'string') {
      value = (event.target as any).value
    }
    this.validateUserDateInput(value)
  }

  validateUserDateInput(value: string) {
    if (!value || !moment(value, DATE_FORMAT, true).isValid()) {
      this.dateOfBirthFormGroup.controls.dateOfBirth.setErrors({ invalid: true })
    } else {
      return null
    }
  }
}
